import React, { useState, useEffect } from 'react';
import { CreditCard, FileText, Clock, ChevronRight, LogOut, ArrowLeft, Sun, Moon, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import WaitlistForm from '@/components/WaitlistForm';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

function MyAccountPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [credits, setCredits] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [creditsRes, reportsRes] = await Promise.all([
        axios.get(`${API}/reports/credits`, { withCredentials: true }),
        axios.get(`${API}/reports/saved`, { withCredentials: true }),
      ]);
      setCredits(creditsRes.data);
      setReports(reportsRes.data.reports || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const buyCredits = async () => {
    // Paid full history reports are temporarily disabled — see /api/reports/checkout
    // (returns 503 when PAID_REPORTS_ENABLED !== 'true').
    toast.message("Coming soon — the full history check isn't on sale yet.");
  };

  const viewReport = (reportId) => {
    navigate(`/report/${reportId}`);
  };

  const fmt = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center transition-colors">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 transition-colors">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" data-testid="back-home-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Home
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              data-testid="theme-toggle-btn"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <span className="text-gray-500 dark:text-gray-400 text-sm">{user?.name}</span>
            <Button onClick={onLogout} variant="outline" size="sm" className="border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-1" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-10 max-w-3xl">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>My Account</h1>

        {/* Credits Balance */}
        <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 mb-6" data-testid="credits-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-500/10 p-3 rounded-full">
                  <CreditCard className="h-6 w-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Report Credits</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white" data-testid="credit-balance">
                    {credits?.credits_remaining ?? 0}
                  </p>
                </div>
              </div>
              {credits?.expires_at && !credits?.expired && (
                <p className="text-gray-400 dark:text-gray-500 text-xs">
                  Expires {fmt(credits.expires_at)}
                </p>
              )}
            </div>
            <div className="rounded-lg border border-yellow-400/40 dark:border-yellow-500/30 bg-yellow-50/60 dark:bg-yellow-900/10 p-4" data-testid="account-waitlist-card">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Full history check: coming soon</p>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                New paid reports are paused while we rebuild. Any credits above still work — redeem them from a vehicle result page. Join the launch list to be notified when it reopens.
              </p>
              <WaitlistForm source="account" compact />
            </div>
          </CardContent>
        </Card>

        {/* Saved Reports */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-yellow-500" />
            Saved Reports
          </h2>
          {reports.length === 0 ? (
            <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
              <CardContent className="p-6 text-center">
                <p className="text-gray-400 dark:text-gray-500">No reports yet. Search a vehicle and unlock a full report.</p>
                <Button onClick={() => navigate('/')} className="mt-4 bg-yellow-500 hover:bg-yellow-400 text-gray-900">
                  Search Vehicle
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {reports.map((report) => (
                <Card
                  key={report.id}
                  className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 cursor-pointer transition-all"
                  onClick={() => viewReport(report.id)}
                  data-testid={`report-${report.registration}`}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <div className="inline-block bg-yellow-500/20 px-3 py-1 rounded mb-1">
                        <span className="font-bold text-yellow-600 dark:text-yellow-500 tracking-wider">{report.registration}</span>
                      </div>
                      <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                        Generated {fmt(report.created_at)} · Expires {fmt(report.expires_at)}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-600" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyAccountPage;
